import * as React from 'react';
import { pair, fromPairs } from 'ramda';
import EDForm from '../../../../../BaseComponents/Forms/EDForm';
import Field from '../../../../../BaseComponents/Forms/EDField';
import EDPaper from '../../../../../BaseComponents/Paper/EDPaper';
import EDView from '../../../../../BaseComponents/View/EDView';
import EDGrid from '../../../../../BaseComponents/GridComponents/EDGrid';
import EDButton from '../../../../../BaseComponents/Buttons/Button/EDButton';
import {
  IValues as IObjectUpdateMutationValues,
  ObjectUpdateMutationReturnType,
} from '../../../../../Features/Delsystem/_StandardRutin/Mutations/ObjectMutations/ObjectUpdateMutationBase';

import { IAttributeField } from '../ConnectedAttributeGroup/ConnectedAttributeGroup';
import EditableAttribute from '../../../EditableAttribute/EditableAttribute';
import {
  IFormValues,
  IWarnings,
  IFormActions,
} from '../../../../../Types/FormTypes';
import Logger from '../../../../../Utils/Logger';
import { IClientBusinessModel } from '../../../../../Types/ClientModelTypes';

interface IProps {
  collection: IClientBusinessModel;
  attributeFields: IAttributeField[];
  loading: boolean;
  mutate: (
    values: IObjectUpdateMutationValues
  ) => ObjectUpdateMutationReturnType;
}

interface IState {
  editMode: boolean;
}

/**
 * En grupperingsvy över attribut med en gemensam redigeringsknapp för att ändra deras värden.
 */
class AttributeGroup extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      editMode: false,
    };
  }

  enterEditMode = () => {
    this.setState({ editMode: true });
  };

  exitEditMode = () => {
    this.setState({ editMode: false });
  };

  handleResetClick = (resetFn: () => void) => () => {
    resetFn();
    this.setState({ editMode: false });
  };

  getValuesFromAttributeFields = (
    attrFields: IAttributeField[]
  ): { [key: string]: string | number | boolean | null } =>
    fromPairs(
      attrFields.map(attrField =>
        pair(attrField.attributePath, attrField.value)
      )
    );

  render() {
    const values = this.getValuesFromAttributeFields(
      this.props.attributeFields
    );

    return (
      <EDForm
        initialValues={values}
        validateOnChange
        onSubmit={(
          values: IFormValues,
          warnings: IWarnings,
          formActions: IFormActions
        ) => {
          this.props
            .mutate(values)
            .then(() => {
              this.exitEditMode();
              formActions.setSubmitting(false);
            })
            .catch(error => {
              formActions.setSubmitting(false);
              Logger.error(error);
            });
        }}
        enableReinitialize
      >
        {formProps => (
          <div style={{ width: '100%', margin: '10px 0' }}>
            <EDPaper elevation={2}>
              <EDView gutter>
                <EDGrid container>
                  {this.props.attributeFields.map(attrField => {
                    return (
                      <Field
                        name={attrField.attributePath}
                        key={attrField.attributePath}
                      >
                        {({ onChange, onBlur, value }) => {
                          return (
                            <EDGrid item xs={12} md={6} lg={2}>
                              <EditableAttribute
                                collection={this.props.collection}
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                editMode={this.state.editMode}
                                attribute={attrField.attribute}
                                debounceDelayTime={attrField.debounceDelayTime}
                              />
                            </EDGrid>
                          );
                        }}
                      </Field>
                    );
                  })}
                </EDGrid>
                {this.state.editMode && (
                  <EDButton
                    label="Spara"
                    onClick={formProps.submitForm}
                    disabled={!formProps.isValid || formProps.isSubmitting}
                  />
                )}
                {this.state.editMode && (
                  <EDButton
                    label="Avbryt"
                    onClick={this.handleResetClick(formProps.handleReset)}
                  />
                )}
                {!this.state.editMode && (
                  <EDButton
                    label="Redigera"
                    onClick={this.enterEditMode}
                    disabled={this.props.loading}
                  />
                )}
              </EDView>
            </EDPaper>
          </div>
        )}
      </EDForm>
    );
  }
}

export default AttributeGroup;
